import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Paper,
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  Person,
  Save,
  Cancel,
  ChildCare,
  Lock,
  AutoAwesome,
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

interface DietaryTag {
  tag_id: number;
  tag_name: string;
  type: string;
}

interface MealSchedule {
  [key: string]: string[];
}

interface IngredientDislike {
  dislike_id: number;
  ingredient_name: string;
  created_at: string;
}

interface FamilyMember {
  member_id: number;
  name: string;
  is_child?: boolean;
  dietary_tags?: DietaryTag[];
  meal_schedule?: MealSchedule;
  ingredient_dislikes?: IngredientDislike[];
}

type WeekSchedule = {
  [key in 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday']: string[];
};

const FamilyMembers: React.FC = () => {
  const { user } = useAuth();
  const isPremium = user?.membership?.status === 'paid';
  const navigate = useNavigate();
  const [members, setMembers] = useState<FamilyMember[]>([]);
  // Onboarding only exposes restriction / medical / allergy types.
  // The full tag table also contains 'cuisine', 'goal', and 'user_generated',
  // which would clutter the family-member edit modal — filter those out.
  // (See web/src/features/onboarding/FamilyStep.tsx for the matching structure.)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingMember, setEditingMember] = useState<FamilyMember | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [availableTags, setAvailableTags] = useState<DietaryTag[]>([]);
  const restrictionTags = availableTags.filter(t => t.type === 'restriction');
  const medicalTags = availableTags.filter(t => t.type === 'medical');
  const allergyTags = availableTags.filter(t => t.type === 'allergy');

  // Form state for add/edit
  const [formData, setFormData] = useState<{
    name: string;
    isChild: boolean;
    dietaryTags: number[];
    mealSchedule: WeekSchedule;
    otherAllergies: string[];
    ingredientDislikes: string[];
  }>({
    name: '',
    isChild: false,
    dietaryTags: [],
    mealSchedule: {
      Monday: [],
      Tuesday: [],
      Wednesday: [],
      Thursday: [],
      Friday: [],
      Saturday: [],
      Sunday: [],
    },
    otherAllergies: [],
    ingredientDislikes: [],
  });
  const [newAllergy, setNewAllergy] = useState('');
  const [newDislike, setNewDislike] = useState('');

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const mealTypes = ['breakfast', 'lunch', 'dinner'];

  // Fetch family members
  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/family-members');
      setMembers(response.data.familyMembers || []);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load family members');
    } finally {
      setLoading(false);
    }
  };

  // Fetch dietary tags
  const fetchDietaryTags = async () => {
    try {
      const response = await api.get('/dietary-tags');
      setAvailableTags(response.data);
    } catch (err) {
      console.error('Failed to fetch dietary tags:', err);
    }
  };

  useEffect(() => {
    fetchMembers();
    fetchDietaryTags();
  }, []);

  // Handle edit click
  const handleEdit = (member: FamilyMember) => {
    setEditingMember(member);

    // Parse meal schedule from backend format
    const schedule: WeekSchedule = {
      Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [], Sunday: []
    };

    if (member.meal_schedule) {
      Object.entries(member.meal_schedule).forEach(([day, meals]) => {
        if (day in schedule) {
          schedule[day as keyof WeekSchedule] = meals;
        }
      });
    }

    setFormData({
      name: member.name,
      isChild: member.is_child || false,
      dietaryTags: member.dietary_tags?.map(tag => tag.tag_id) || [],
      mealSchedule: schedule,
      otherAllergies: [],
      ingredientDislikes: member.ingredient_dislikes?.map(d => d.ingredient_name) || [],
    });
    setNewAllergy('');
    setNewDislike('');
  };

  // Handle save (update)
  const handleSave = async () => {
    if (!editingMember) return;

    try {
      // Format dietary restrictions for backend
      const dietaryRestrictions = formData.dietaryTags.map(tagId => ({
        tag_id: tagId,
        severity: 'strict',
      }));

      // Format meal schedule for backend: transform day→meals[] to meal→days[]
      const mealSchedule: { [key: string]: string[] } = {
        breakfast: [],
        lunch: [],
        dinner: [],
      };
      Object.entries(formData.mealSchedule).forEach(([day, meals]) => {
        meals.forEach(meal => {
          mealSchedule[meal].push(day.toLowerCase());
        });
      });

      await api.put(`/family-members/${editingMember.member_id}`, {
        name: formData.name,
        is_child: formData.isChild,
        dietary_restrictions: dietaryRestrictions,
        meal_schedule: mealSchedule,
      });

      // Save ingredient dislikes (combine other allergies with dislikes)
      const allDislikes = [...formData.otherAllergies, ...formData.ingredientDislikes];
      await api.put(`/family-members/${editingMember.member_id}/ingredient-dislikes`, {
        ingredient_names: allDislikes,
      });

      setEditingMember(null);
      setNewAllergy('');
      setNewDislike('');
      fetchMembers();
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update family member');
    }
  };

  // Handle add new member
  const handleAdd = async () => {
    try {
      // Format dietary restrictions for backend
      const dietaryRestrictions = formData.dietaryTags.map(tagId => ({
        tag_id: tagId,
        severity: 'strict',
      }));

      // Format meal schedule for backend: transform day→meals[] to meal→days[]
      const mealSchedule: { [key: string]: string[] } = {
        breakfast: [],
        lunch: [],
        dinner: [],
      };
      Object.entries(formData.mealSchedule).forEach(([day, meals]) => {
        meals.forEach(meal => {
          mealSchedule[meal].push(day.toLowerCase());
        });
      });

      const response = await api.post('/family-members', {
        name: formData.name,
        is_child: formData.isChild,
        dietary_restrictions: dietaryRestrictions,
        meal_schedule: mealSchedule,
      });

      // Save ingredient dislikes for new member (combine other allergies with dislikes)
      const newMemberId = response.data.familyMember?.member_id;
      const allDislikes = [...formData.otherAllergies, ...formData.ingredientDislikes];
      if (newMemberId && allDislikes.length > 0) {
        await api.put(`/family-members/${newMemberId}/ingredient-dislikes`, {
          ingredient_names: allDislikes,
        });
      }

      setIsAddDialogOpen(false);
      fetchMembers();
      setError(null);

      // Reset form
      setFormData({
        name: '',
        isChild: false,
        dietaryTags: [],
        mealSchedule: {
          Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [],
          Saturday: [], Sunday: [],
        },
        otherAllergies: [],
        ingredientDislikes: [],
      });
      setNewAllergy('');
      setNewDislike('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add family member');
    }
  };

  // Handle delete
  const handleDelete = async (memberId: number) => {
    try {
      await api.delete(`/family-members/${memberId}`);
      setDeleteConfirm(null);
      fetchMembers();
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete family member');
    }
  };

  // Handle meal schedule change
  const handleMealScheduleChange = (day: keyof WeekSchedule, meal: string) => {
    setFormData(prev => ({
      ...prev,
      mealSchedule: {
        ...prev.mealSchedule,
        [day]: prev.mealSchedule[day].includes(meal)
          ? prev.mealSchedule[day].filter((m: string) => m !== meal)
          : [...prev.mealSchedule[day], meal],
      },
    }));
  };

  // Toggle a dietary tag id in formData.dietaryTags
  const toggleDietaryTag = (tagId: number) => {
    setFormData(prev => ({
      ...prev,
      dietaryTags: prev.dietaryTags.includes(tagId)
        ? prev.dietaryTags.filter(id => id !== tagId)
        : [...prev.dietaryTags, tagId],
    }));
  };

  // Renders one labeled section of checkbox tags (e.g., "Allergies").
  // Matches the structure used in onboarding's FamilyStep.tsx.
  const renderTagSection = (label: string, tags: DietaryTag[]) => {
    if (tags.length === 0) return null;
    return (
      <Box sx={{ mb: 2 }}>
        <FormLabel component="legend" sx={{ mb: 0.5 }}>{label}</FormLabel>
        <FormGroup row>
          {tags.map((tag) => (
            <FormControlLabel
              key={tag.tag_id}
              control={
                <Checkbox
                  size="small"
                  checked={formData.dietaryTags.includes(tag.tag_id)}
                  onChange={() => toggleDietaryTag(tag.tag_id)}
                />
              }
              label={tag.tag_name}
            />
          ))}
        </FormGroup>
      </Box>
    );
  };

  // Handle ingredient dislikes
  const handleAddDislike = () => {
    const trimmed = newDislike.trim().toLowerCase();
    if (trimmed && !formData.ingredientDislikes.some(d => d.toLowerCase() === trimmed)) {
      setFormData(prev => ({
        ...prev,
        ingredientDislikes: [...prev.ingredientDislikes, trimmed],
      }));
      setNewDislike('');
    }
  };

  // Handle other allergies
  const handleAddAllergy = () => {
    const trimmed = newAllergy.trim().toLowerCase();
    if (trimmed && !formData.otherAllergies.some(a => a.toLowerCase() === trimmed) && !formData.ingredientDislikes.some(d => d.toLowerCase() === trimmed)) {
      setFormData(prev => ({
        ...prev,
        otherAllergies: [...prev.otherAllergies, trimmed],
      }));
      setNewAllergy('');
    }
  };

  const handleRemoveAllergy = (ingredient: string) => {
    setFormData(prev => ({
      ...prev,
      otherAllergies: prev.otherAllergies.filter(a => a !== ingredient),
    }));
  };

  const handleRemoveDislike = (ingredient: string) => {
    setFormData(prev => ({
      ...prev,
      ingredientDislikes: prev.ingredientDislikes.filter(d => d !== ingredient),
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, px: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Family Members</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setFormData({
              name: '',
              isChild: false,
              dietaryTags: [],
              mealSchedule: {
                Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [],
                Saturday: [], Sunday: [],
              },
              otherAllergies: [],
              ingredientDislikes: [],
            });
            setNewAllergy('');
            setNewDislike('');
            setIsAddDialogOpen(true);
          }}
        >
          Add Family Member
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {members.map((member) => (
          <Box key={member.member_id} sx={{ flexBasis: { xs: '100%', md: 'calc(50% - 12px)', lg: 'calc(33.333% - 16px)' } }}>
            <Card>
              <CardContent>
                {editingMember?.member_id === member.member_id ? (
                  // Edit mode
                  <Box>
                    <TextField
                      fullWidth
                      label="Name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      sx={{ mb: 2 }}
                    />

                    <Paper
                      sx={{
                        p: 1.5, mb: 2, cursor: 'pointer',
                        bgcolor: formData.isChild ? '#fffbeb' : 'transparent',
                        border: '1px solid',
                        borderColor: formData.isChild ? '#fcd34d' : '#e0e0e0',
                        transition: 'all 0.2s',
                      }}
                      onClick={() => setFormData({ ...formData, isChild: !formData.isChild })}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={formData.isChild}
                            icon={<ChildCare sx={{ color: '#bbb' }} />}
                            checkedIcon={<ChildCare sx={{ color: '#f59e0b' }} />}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>Kid-Friendly Meals</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Generate simpler, more familiar meals for children
                            </Typography>
                          </Box>
                        }
                        sx={{ m: 0 }}
                      />
                    </Paper>

                    {/* Dietary tag sections — three labeled groups matching onboarding's FamilyStep.
                        Replaces a single Select that mixed in cuisine/goal/user_generated types. */}
                    {renderTagSection('Dietary restrictions', restrictionTags)}
                    {renderTagSection('Medical nutrition needs', medicalTags)}
                    {renderTagSection('Allergies', allergyTags)}

                    {/* Meal Schedule (Premium feature — free users see it greyed out as an upgrade nudge) */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <FormLabel component="legend">Meal Schedule</FormLabel>
                      {!isPremium && (
                        <Chip
                          icon={<Lock sx={{ fontSize: 12 }} />}
                          label="Premium"
                          size="small"
                          color="primary"
                          sx={{ height: 20, '& .MuiChip-label': { fontSize: '0.7rem', px: 0.75 } }}
                        />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      {isPremium
                        ? 'Select which meals this person will eat each day'
                        : 'Premium meal plans tailor to each person’s schedule. Upgrade to customize.'}
                    </Typography>
                    <Paper
                      sx={{
                        p: 2,
                        mb: 1,
                        maxHeight: 300,
                        overflowY: 'auto',
                        opacity: isPremium ? 1 : 0.55,
                        cursor: isPremium ? 'auto' : 'pointer',
                      }}
                      onClick={isPremium ? undefined : () => navigate('/membership')}
                    >
                      {daysOfWeek.map((day) => (
                        <Box key={day} sx={{ mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '0.85rem' }}>
                            {day}
                          </Typography>
                          <FormGroup row>
                            {mealTypes.map((meal) => (
                              <FormControlLabel
                                key={`${day}-${meal}`}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={formData.mealSchedule[day as keyof WeekSchedule].includes(meal)}
                                    onChange={() => handleMealScheduleChange(day as keyof WeekSchedule, meal)}
                                    disabled={!isPremium}
                                  />
                                }
                                label={meal.charAt(0).toUpperCase() + meal.slice(1)}
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
                              />
                            ))}
                          </FormGroup>
                        </Box>
                      ))}
                    </Paper>
                    {!isPremium && (
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<AutoAwesome />}
                        sx={{ mb: 2, borderStyle: 'dashed' }}
                        onClick={() => navigate('/membership')}
                      >
                        Unlock with Premium
                      </Button>
                    )}

                    <FormLabel component="legend" sx={{ mb: 1, mt: 2, color: '#c62828' }}>Other Allergies</FormLabel>
                    <Paper sx={{ p: 2, mb: 2, bgcolor: '#fff5f5', border: '1px solid #ffcdd2' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.85rem' }}>
                        Add allergies not in the list above - we'll strictly avoid these ingredients
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <TextField
                          size="small"
                          placeholder="e.g., sesame, shellfish..."
                          value={newAllergy}
                          onChange={(e) => setNewAllergy(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleAddAllergy()}
                          sx={{ flex: 1 }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          sx={{ bgcolor: '#c62828', '&:hover': { bgcolor: '#b71c1c' } }}
                          onClick={handleAddAllergy}
                          disabled={!newAllergy.trim()}
                        >
                          Add
                        </Button>
                      </Box>
                      {formData.otherAllergies.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {formData.otherAllergies.map((ingredient, idx) => (
                            <Chip
                              key={idx}
                              label={ingredient}
                              size="small"
                              color="error"
                              onDelete={() => handleRemoveAllergy(ingredient)}
                            />
                          ))}
                        </Box>
                      )}
                    </Paper>

                    <FormLabel component="legend" sx={{ mb: 1, mt: 2 }}>Ingredient Dislikes (Optional)</FormLabel>
                    <Paper sx={{ p: 2, mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.85rem' }}>
                        Add ingredients this person won't eat - we'll avoid them in meal plans
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <TextField
                          size="small"
                          placeholder="e.g., mushrooms, olives..."
                          value={newDislike}
                          onChange={(e) => setNewDislike(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleAddDislike()}
                          sx={{ flex: 1 }}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleAddDislike}
                          disabled={!newDislike.trim()}
                        >
                          Add
                        </Button>
                      </Box>
                      {formData.ingredientDislikes.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {formData.ingredientDislikes.map((ingredient, idx) => (
                            <Chip
                              key={idx}
                              label={ingredient}
                              size="small"
                              variant="outlined"
                              onDelete={() => handleRemoveDislike(ingredient)}
                            />
                          ))}
                        </Box>
                      )}
                    </Paper>

                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        startIcon={<Cancel />}
                        onClick={() => setEditingMember(null)}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<Save />}
                        onClick={handleSave}
                      >
                        Save
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  // View mode
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Person sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">{member.name}</Typography>
                      {member.is_child && (
                        <Chip
                          icon={<ChildCare />}
                          label="Child"
                          size="small"
                          sx={{ ml: 1, bgcolor: '#fef3c7', color: '#92400e', '& .MuiChip-icon': { color: '#f59e0b' } }}
                        />
                      )}
                    </Box>

                    {member.dietary_tags && member.dietary_tags.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Dietary Restrictions:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {member.dietary_tags.map((tag) => (
                            <Chip
                              key={tag.tag_id}
                              label={tag.tag_name}
                              size="small"
                              color={tag.type === 'allergy' ? 'error' : 'primary'}
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {member.meal_schedule && Object.keys(member.meal_schedule).length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Meal Schedule:
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {Object.entries(member.meal_schedule)
                            .filter(([_, meals]) => meals.length > 0)
                            .map(([day, meals]) => (
                              <Typography key={day} variant="body2" sx={{ fontSize: '0.875rem' }}>
                                <strong>{day}:</strong> {meals.join(', ')}
                              </Typography>
                            ))}
                        </Box>
                      </Box>
                    )}

                    {member.ingredient_dislikes && member.ingredient_dislikes.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Won't Eat:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {member.ingredient_dislikes.map((dislike) => (
                            <Chip
                              key={dislike.dislike_id}
                              label={dislike.ingredient_name}
                              size="small"
                              color="error"
                              variant="outlined"
                              sx={{ borderStyle: 'dashed' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <IconButton size="small" onClick={() => handleEdit(member)}>
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteConfirm(member.member_id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog
        open={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Family Member</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mb: 3, mt: 1 }}
          />

          <Paper
            sx={{
              p: 2, mb: 3, cursor: 'pointer',
              bgcolor: formData.isChild ? '#fffbeb' : 'transparent',
              border: '1px solid',
              borderColor: formData.isChild ? '#fcd34d' : '#e0e0e0',
              transition: 'all 0.2s',
            }}
            onClick={() => setFormData({ ...formData, isChild: !formData.isChild })}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isChild}
                  icon={<ChildCare sx={{ color: '#bbb' }} />}
                  checkedIcon={<ChildCare sx={{ color: '#f59e0b' }} />}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Kid-Friendly Meals</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Generate simpler, more familiar meals for children
                  </Typography>
                </Box>
              }
              sx={{ m: 0 }}
            />
          </Paper>

          {/* Dietary tag sections — three labeled groups matching onboarding's FamilyStep. */}
          {renderTagSection('Dietary restrictions', restrictionTags)}
          {renderTagSection('Medical nutrition needs', medicalTags)}
          {renderTagSection('Allergies', allergyTags)}

          {/* Meal Schedule (Premium feature — free users see it greyed out as an upgrade nudge) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <FormLabel component="legend">Meal Schedule</FormLabel>
            {!isPremium && (
              <Chip
                icon={<Lock sx={{ fontSize: 12 }} />}
                label="Premium"
                size="small"
                color="primary"
                sx={{ height: 20, '& .MuiChip-label': { fontSize: '0.7rem', px: 0.75 } }}
              />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {isPremium
              ? 'Select which meals this person will eat each day'
              : 'Premium meal plans tailor to each person’s schedule. Upgrade to customize.'}
          </Typography>
          <Paper
            sx={{
              p: 2,
              opacity: isPremium ? 1 : 0.55,
              cursor: isPremium ? 'auto' : 'pointer',
            }}
            onClick={isPremium ? undefined : () => navigate('/membership')}
          >
            {daysOfWeek.map((day) => (
              <Box key={day} sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {day}
                </Typography>
                <FormGroup row>
                  {mealTypes.map((meal) => (
                    <FormControlLabel
                      key={`${day}-${meal}`}
                      control={
                        <Checkbox
                          checked={formData.mealSchedule[day as keyof WeekSchedule].includes(meal)}
                          onChange={() => handleMealScheduleChange(day as keyof WeekSchedule, meal)}
                          disabled={!isPremium}
                        />
                      }
                      label={meal.charAt(0).toUpperCase() + meal.slice(1)}
                    />
                  ))}
                </FormGroup>
              </Box>
            ))}
          </Paper>
          {!isPremium && (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AutoAwesome />}
              sx={{ mt: 1, borderStyle: 'dashed' }}
              onClick={() => navigate('/membership')}
            >
              Unlock with Premium
            </Button>
          )}

          <FormLabel component="legend" sx={{ mb: 2, mt: 3, color: '#c62828' }}>Other Allergies</FormLabel>
          <Paper sx={{ p: 2, bgcolor: '#fff5f5', border: '1px solid #ffcdd2' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add allergies not in the list above - we'll strictly avoid these ingredients
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                size="small"
                placeholder="e.g., sesame, shellfish..."
                value={newAllergy}
                onChange={(e) => setNewAllergy(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddAllergy()}
                sx={{ flex: 1 }}
              />
              <Button
                variant="contained"
                sx={{ bgcolor: '#c62828', '&:hover': { bgcolor: '#b71c1c' } }}
                onClick={handleAddAllergy}
                disabled={!newAllergy.trim()}
              >
                Add
              </Button>
            </Box>
            {formData.otherAllergies.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {formData.otherAllergies.map((ingredient, idx) => (
                  <Chip
                    key={idx}
                    label={ingredient}
                    size="small"
                    color="error"
                    onDelete={() => handleRemoveAllergy(ingredient)}
                  />
                ))}
              </Box>
            )}
          </Paper>

          <FormLabel component="legend" sx={{ mb: 2, mt: 3 }}>Ingredient Dislikes (Optional)</FormLabel>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add ingredients this person won't eat - we'll avoid them in meal plans
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                size="small"
                placeholder="e.g., mushrooms, olives..."
                value={newDislike}
                onChange={(e) => setNewDislike(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddDislike()}
                sx={{ flex: 1 }}
              />
              <Button
                variant="outlined"
                onClick={handleAddDislike}
                disabled={!newDislike.trim()}
              >
                Add
              </Button>
            </Box>
            {formData.ingredientDislikes.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {formData.ingredientDislikes.map((ingredient, idx) => (
                  <Chip
                    key={idx}
                    label={ingredient}
                    size="small"
                    variant="outlined"
                    onDelete={() => handleRemoveDislike(ingredient)}
                  />
                ))}
              </Box>
            )}
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd}>Add Member</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this family member? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FamilyMembers;